import dayjs from "dayjs";
import type {
  ExpediteDetails,
  ServiceFailureDetails,
  ServiceFailureReasonCode,
} from "../../pages/linehaul/ucccOfd/ucccOfdRightPanel/ucccOfdDetails/ucccOfdRightPanelStatusDetails.type";
import type { UcccOfdShipmentInformation } from "../../pages/linehaul/ucccOfd/ucccOfdShipmentInformation.type";
import type {
  UcccOfdShipmentRow,
  UcccOfdShipmentsListResponse,
} from "../../pages/linehaul/ucccOfd/ucccOfdShipmentRow.type";
import type { UcccOfdDashboardSummaryData } from "../../pages/linehaul/ucccOfd/ucccOfdDashboard/ucccOfdDashboardSummary.type";
import type { TripDetail } from "../../hooks/react-query/linehaul/useTrip";
import { mockTransitDashboardTripDetails } from "./transitDashboard.mocks";

// TODO(LH-2806): [FE][API] Integrate Table Data and Sorting API — list mock mirrors partial API contract; extend when sorting/pagination responses are defined (see handlers MSW + useUcccOfdShipments).

export const mockUcccOfdShipments = {
  default: {
    totalCount: 473,
    shipments: [
      {
        id: "110930229",
        shipmentId: 110930229,
        proNumber: "134842651",
        pickupDateUtc: dayjs("2024-08-09T07:00:00Z"),
        dueDate: dayjs("2024-08-16"),
        deliveryDateUtc: dayjs("2024-08-16T19:00:00Z"),
        appointmentStartTimestampUtc: dayjs("2024-08-17T19:00:00Z"),
        originSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 5,
          timeZone: "America/New_York",
        },
        destinationSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        currentSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        finalDestination: "ATL",
        pts: 1,
        pcs: 2,
        weight: 1452,
        shipmentTags: ["CC", "UC", "APTM"],
        status: "ENRT",
        tripNumber: 756394,
        etaUtc: dayjs("2024-08-16T19:00:00Z"),
        ataUtc: dayjs("2024-08-16T18:50:00Z"),
        etaStatus: "ON TIME",
        locationDetails: {
          state: "TN",
          city: "Mount Pleasant",
          zipCode: "38647",
          locationSic: "ATL",
        },
        consignee: {
          id: 87520,
          name: "CAPE MARINE INC",
          address: "1427 N Main ST",
          city: "Mount Pleasant",
          state: "TN",
          zipCode: "38647",
        },
        noDimensions: false,
        serviceFailure: false,
        expediteShipment: true,
        hasProWarning: true,
        arrivalToSic: "8/16/24 1500",
        arrivalToSicStatus: "ON TIME",
        appt: "8/17/24 1500",
        fbFollowUpStatus: "PENDING APPT",
        currentZone: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "New Jersey",
        },
      },
      {
        id: "110930230",
        shipmentId: 110930230,
        proNumber: "110930230",
        pickupDateUtc: dayjs("2024-08-09T07:00:00Z"),
        dueDate: dayjs("2024-08-16"),
        deliveryDateUtc: dayjs("2024-08-16T19:00:00Z"),
        appointmentStartTimestampUtc: dayjs("2024-08-17T19:00:00Z"),
        originSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 5,
          timeZone: "America/New_York",
        },
        destinationSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        currentSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        finalDestination: "ATL",
        pts: 1,
        pcs: 2,
        weight: 1452,
        shipmentTags: ["CC", "UC", "APTM"],
        status: "ENRT",
        tripNumber: 756394,
        etaUtc: dayjs("2024-08-16T19:00:00Z"),
        ataUtc: dayjs("2024-08-16T20:15:00Z"),
        etaStatus: "LATE",
        locationDetails: {
          state: "TN",
          city: "Mount Pleasant",
          zipCode: "38647",
          locationSic: "ATL",
        },
        consignee: {
          id: 87520,
          name: "CAPE MARINE INC",
          address: "1427 N Main ST",
          city: "Mount Pleasant",
          state: "TN",
          zipCode: "38647",
        },
        noDimensions: false,
        serviceFailure: true,
        expediteShipment: true,
        hasProWarning: true,
        arrivalToSic: "8/16/24 1500",
        arrivalToSicStatus: "LATE",
        appt: "8/17/24 1500",
        fbFollowUpStatus: "PENDING APPT",
        currentZone: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "New Jersey",
        },
      },
    ] satisfies UcccOfdShipmentRow[],
  },
} satisfies Record<string, UcccOfdShipmentsListResponse>;

export const mockUcccOfdDashboardSummary = {
  default: {
    totalUcccShipmentCount: 23,
    onTimePerformancePercent: 97.4,
    onTimePerformanceOnCount: 22,
    onTimePerformanceOfCount: 23,
    serviceFailureCount: 19,
    deliveredCount: 3,
    fbFollowUpStatusCounts: {
      delivered: 3,
      expedite: 4,
      ofd: 3,
      osd: 0,
      pendingAppt: 3,
      pendingPod: 2,
      servFailure: 0,
      noStatus: 5,
    },
  },
} as const satisfies Record<string, UcccOfdDashboardSummaryData>;

export const mockExpediteDetails = {
  default: {
    type: "carrier" as const,
    carrierLabel: "L004 - COVENANT TRANSPORT INC",
    amount: 80,
    expediteReasonNote: "CCHILDS - 10:33 AM - 5/17/23",
    carrierId: 95128,
  },
} as const satisfies Record<string, ExpediteDetails>;

export const mockServiceFailureDetails = {
  default: {
    failureReasonCode: "Daylight Failure",
    subReason: "Late OB Close",
    description: "Origin ATL SIC cut the load out late",
    noteTimestamp: "CICHLIDS - 10:33 AM - 5/17/23",
  },
} as const satisfies Record<string, ServiceFailureDetails>;

export type DimItem = {
  lengthIn: number;
  widthIn: number;
  heightIn: number;
};

type UcccOfdShipmentDetailsMock = {
  shipment: UcccOfdShipmentInformation;
  dimensions: DimItem[];
};

const mockUcccOfdShipmentDetailsDimensions: DimItem[] = [
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 22, widthIn: 36, heightIn: 50 },
  { lengthIn: 39, widthIn: 42, heightIn: 47 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 39, widthIn: 42, heightIn: 50 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
];

export const mockUcccOfdShipmentDetails = {
  default: {
    shipment: {
      proNumber: "180403917",
      status: "ENRT",
      statusStartDateUtc: "2026-02-02T08:19:10.211Z",
      shipmentTags: ["UC", "CC", "RESI", "APTM"],
      originSic: { code: "CHT", zipCode: "28206" },
      destinationSic: { code: "ONT", zipCode: "92337" },
      serviceDue: "2026-02-06",
      appointmentStartTimeUtc: null,
      appointmentEndTimeUtc: null,
      pallets: 1,
      pieces: 0,
      weight: 287,
      shippingInfo: {
        shipper: {
          name: "SPROGS",
          address: "716 N SECREST AVE",
          city: "MONROE",
          state: "NC",
          zipCode: "28110",
          phoneNumber: "",
        },
        consignee: {
          name: "MARGUERITTE MARSHALL",
          address: "80455 CAMINO SAN LUCAS",
          city: "INDIO",
          state: "CA",
          zipCode: "92203",
          phoneNumber: "425 985 4088",
        },
      },
      descriptionOfArticles: [
        {
          commodityCode: "79300-3",
          description: "LOCKERS CL250",
          rateClass: "125",
          actualClass: "250",
        },
      ],
    },
    dimensions: mockUcccOfdShipmentDetailsDimensions,
  },
} satisfies Record<string, UcccOfdShipmentDetailsMock>;

export function getMockUcccOfdTripDetail(tripNumber: number): TripDetail {
  const base = mockTransitDashboardTripDetails.default;
  return {
    ...base,
    tripNumber,
    originSic: {
      id: 1,
      code: "ATB",
      ownership: "C",
      lhRegionId: 1,
      timeZone: "America/New_York",
    },
    destinationSic: {
      id: 2,
      code: "ONT",
      ownership: "C",
      lhRegionId: 2,
      timeZone: "America/Los_Angeles",
    },
    employeeDrivers: [],
    stops: [...base.stops],
    deadheads: base.deadheads.map((d) => ({ ...d })),
    accessorials: base.accessorials.map((a) => ({ ...a })),
    stopToStopMileages: [...base.stopToStopMileages],
    shipmentDestinationDetails: [...base.shipmentDestinationDetails],
    shipmentsWithIssues: [...base.shipmentsWithIssues],
    groupedShipmentsByStop: base.groupedShipmentsByStop.map((stop) => ({
      ...stop,
      data: { ...stop.data },
    })),
    shipments: [],
    totalBills: 0,
    driverCheckInDetails: [],
    isPublished: true,
    isCancelled: false,
    hasLoadPair: false,
    totalRevenue: 4000,
    totalCost: 3200,
    netLinehaul: 800,
    rolloverFee: 0,
    isLoadAndHold: false,
  } as TripDetail;
}

export const mockServiceFailureReasonCodes: ServiceFailureReasonCode[] = [
  {
    id: 1,
    failureReasonCode: "AGENT FAILURE",
    subReasons: [
      {
        id: 1,
        subReason: "LATE OB CLOSE",
        explanation: "AGENT CUT THE LOAD OUT LATE",
      },
      {
        id: 2,
        subReason: "OVERFLOW",
        explanation: "ORIGIN TERMINAL OVERFLOWED THE SHIPMENT",
      },
      {
        id: 3,
        subReason: "OVERSIZE",
        explanation:
          "OVERSIZED SHIPMENT WAS TOO LARGE AND HAD TO BE HELD AT ORIGIN RESULTING IN DELAY ",
      },
      {
        id: 4,
        subReason: "CAPACITY",
        explanation:
          "DELAYED DUE TO AGENT ERROR (LACK OF MANPOWER, FREIGHT DELIVERED TO WRONG CONSIGNEE, ETC.)",
      },
      {
        id: 5,
        subReason: "LATE DELIVERY",
        explanation:
          "DELIVERY DRIVER ARRIVED LATE FOR DELIVERY (AFTER 5PM FOR UC OR AFTER SPECIFIED CC WINDOW)",
      },
      {
        id: 6,
        subReason: "MISSED ORIGIN HUB TRANSFER",
        explanation:
          "PICK-UP SERVICE CENTER FAILED TO TRANSFER SHIPMENT TO OB LOADING HUB",
      },
      {
        id: 7,
        subReason: "DEST HELD",
        explanation: "DESTINATION TERMINAL HELD SHIPMENT IN ERROR",
      },
      {
        id: 8,
        subReason: "DELIVERED CC EARLY",
        explanation: "DELIVERED THE CC SHIPMENT BEFORE SPECIFIED TIME WINDOW",
      },
      {
        id: 9,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
  {
    id: 2,
    failureReasonCode: "DAYLIGHT FAILURE",
    subReasons: [
      {
        id: 10,
        subReason: "LATE OB CLOSE",
        explanation: "ORIGIN SERVICE CENTER CUT THE LOAD OUT LATE",
      },
      {
        id: 11,
        subReason: "OVERFLOW",
        explanation: "ORIGIN SERVICE CENTER OVERFLOWED THE SHIPMENT",
      },
      {
        id: 12,
        subReason: "OVERSIZE",
        explanation:
          "OVERSIZED SHIPMENT WAS TOO LARGE AND HAD TO BE HELD AT ORIGIN RESULTING IN DELAY ",
      },
      {
        id: 13,
        subReason: "DEST HELD",
        explanation: "DESTINATION TERMINAL HELD SHIPMENT IN ERROR",
      },
      {
        id: 14,
        subReason: "DRIVER PERFORMANCE",
        explanation: "DELAYED DUE TO DAYLIGHT DRIVER PERFORMANCE RELATED ISSUE",
      },
      {
        id: 15,
        subReason: "MECH",
        explanation:
          "DRIVER HAS A MECHANICAL DELAY (FLAT TIRE, WINDSHIELD REPAIRS, ENGINE FAILURE, ETC.)",
      },
      {
        id: 16,
        subReason: "LATE DELIVERY",
        explanation:
          "DELIVERY DRIVER ARRIVED LATE FOR DELIVERY (MISSED GUARANTEED DELIVERY WINDOW, CC DELIVERY BEFORE 9 AM, ETC.)",
      },
      {
        id: 17,
        subReason: "DELIVERED CC EARLY",
        explanation: "DELIVERED THE CC SHIPMENT BEFORE SPECIFIED TIME WINDOW",
      },
      {
        id: 18,
        subReason: "SVC STANDARD REVIEW",
        explanation: "DELIVERY UNDER REVIEW WITH DIRECTOR",
      },
      {
        id: 19,
        subReason: "SERVICE CENTER ERROR",
        explanation: "SERVICE CENTER RELATED ERROR",
      },
      {
        id: 20,
        subReason: "LINEHAUL ERROR",
        explanation: "LINEHAUL TEAM RELATED ERROR",
      },
      {
        id: 21,
        subReason: "BILLING ERROR",
        explanation:
          "NOT BILLED IN TIME OR BILLED TOO LATE CAUSING LOSS OF VISIBILITY",
      },
      {
        id: 22,
        subReason: "CS ERROR",
        explanation: "CLIENT SERVICES TEAM RELATED ERROR",
      },
      {
        id: 23,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
  {
    id: 3,
    failureReasonCode: "CARRIER FAILURE",
    subReasons: [
      {
        id: 24,
        subReason: "LATE PICKUP",
        explanation: "CARRIER PICKED UP LOAD LATE FROM ORIGIN",
      },
      {
        id: 25,
        subReason: "MECH",
        explanation:
          "CARRIER HAS A MECHANICAL DELAY (FLAT TIRE, WINDSHIELD REPAIRS, ENGINE FAILURE, ETC.)",
      },
      {
        id: 26,
        subReason: "DRIVER PERFORMANCE",
        explanation:
          "DELAYED DUE TO DRIVER PERFORMANCE RELATED ISSUE (FATIGUE, ILLNESS, DRIVER GOT OFF TRUCK)",
      },
      {
        id: 27,
        subReason: "CARRIER PERFORMANCE",
        explanation:
          "DELAYED DUE TO CARRIER RELATED ISSUE (LATE REPOWER, DRIVER HOME TIME, BOOKED LOAD WITH WRONG DELIVERY INFO, ETC.)",
      },
      {
        id: 28,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
];
